/**
 * @graphmind/api-client — typed access to the GraphMind backend.
 *
 * `paths` comes from src/schema.d.ts, generated from docs/api/openapi.json
 * (`npm run generate`). The fetch helpers below cover the core surfaces the
 * frontend needs; anything else can use `request()` with full typing via the
 * schema types.
 */

import type { components, paths } from "./schema";
import { readSseStream, type StreamEvent } from "./sse";

export type { components, paths };
export { SseParser, readSseStream } from "./sse";
export type { StreamEvent } from "./sse";

export type ExplainRequest = components["schemas"]["ExplainRequest"];
export type RagRequest = components["schemas"]["RagRequest"];
export type NodeCreate = components["schemas"]["NodeCreate"];
export type EdgeCreate = components["schemas"]["EdgeCreate"];
export type ExportRequest = components["schemas"]["ExportRequest"];
export type ExportInventory = components["schemas"]["ExportInventory"];
export type TierSelection = components["schemas"]["TierSelection"];
export type WorkspaceItemCreate = components["schemas"]["WorkspaceItemCreate"];
export type RegisterWebRequest = components["schemas"]["RegisterWebRequest"];
export type ContextBundleRequest = components["schemas"]["ContextBundleRequest"];

/** Response of `POST /api/context/bundle` — hand-written because the server
 * types the route's response as a loose object (see schema.d.ts). Text shapes
 * (`markdown`/`index`) carry their body in `bundle.content`; `graphiti`
 * carries structured JSON in `bundle.payload` instead. */
export interface ContextBundleResult {
  bundle: {
    shape: "markdown" | "graphiti" | "index";
    token_estimate: number;
    truncated: boolean;
    content: string | null;
    payload: Record<string, unknown> | null;
    included: string[];
    dropped: string[];
  };
  report: {
    query: string;
    anchors: number;
    expanded: number;
    considered: number;
    returned: number;
    coverage: Record<string, number>;
    blind_spots: string[];
    duration_ms: number;
  };
  documents: Array<{
    document_id: string;
    node_id: string;
    label: string;
    score: number;
    evidence: Array<{ kind: string; via: string | null; weight: number }>;
  }>;
}

export interface ClientOptions {
  baseUrl: string; // e.g. "http://127.0.0.1:8000"
  authToken?: string; // GRAPHMIND_AUTH_TOKEN when the backend runs in token mode
  fetchImpl?: typeof fetch; // injectable for tests
  /** Awaited before each request — the app wires this to a token-freshness gate
   * so the auth cookie is never stale when the request goes out. Kept as a
   * callback so this package stays free of any auth/firebase dependency. */
  beforeRequest?: () => Promise<void>;
  /** Invoked once on a 401/403 to recover the session (refresh the token +
   * rewrite the cookie). Returns whether recovery succeeded, i.e. whether a
   * retry is worthwhile; a falsy result lets the original error surface so the
   * UI can show its signed-out state. */
  onUnauthorized?: () => Promise<boolean | void>;
}

export class GraphMindClient {
  private readonly base: string;
  private readonly fetch: typeof fetch;
  private readonly headers: Record<string, string>;
  private readonly beforeRequest?: () => Promise<void>;
  private readonly onUnauthorized?: () => Promise<boolean | void>;

  constructor(options: ClientOptions) {
    this.base = options.baseUrl.replace(/\/$/, "");
    // Bind: calling window.fetch with `this` = client throws "Illegal
    // invocation" in browsers (Node's undici is indifferent, so tests pass
    // either way — the bind is for real browsers).
    this.fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.headers = {
      "Content-Type": "application/json",
      ...(options.authToken ? { Authorization: `Bearer ${options.authToken}` } : {}),
    };
    this.beforeRequest = options.beforeRequest;
    this.onUnauthorized = options.onUnauthorized;
  }

  /** Low-level escape hatch: typed-by-convention JSON request. */
  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const init: RequestInit = {
      method,
      headers: this.headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    };
    const send = async () => {
      await this.beforeRequest?.();
      return this.fetch(`${this.base}${path}`, init);
    };

    let resp = await send();
    // Self-heal a stale-cookie 401/403: refresh the token and retry exactly once
    // (this single, non-looping branch is the guard) so a genuine sign-out still
    // surfaces instead of retrying forever.
    if ((resp.status === 401 || resp.status === 403) && this.onUnauthorized) {
      if (await this.onUnauthorized()) resp = await send();
    }
    if (!resp.ok) {
      const detail = await resp.text().catch(() => "");
      throw new GraphMindApiError(resp.status, detail);
    }
    return (await resp.json()) as T;
  }

  // --- documents -----------------------------------------------------------

  /** Liveness, plus two operational signals the route has carried since R3b:
   * `embedder.semantic` is false when search fell back to the non-semantic
   * token hash, and `integrity` is the storage heartbeat's in-memory summary.
   * See api/routes.py::health and embeddings/provider.py::embedder_health. */
  health() {
    return this.request<{
      status: string;
      version: string;
      embedder: {
        model_id: string;
        dim: number | null;
        semantic: boolean;
        degraded: boolean;
      };
      integrity: { clean: boolean; issues: number; last_scan: string | null };
    }>("GET", "/api/health");
  }

  listDocuments() {
    return this.request<Record<string, unknown>[]>("GET", "/api/documents");
  }

  getDocument(docId: string) {
    return this.request<Record<string, unknown>>("GET", `/api/documents/${docId}`);
  }

  registerLocalDocument(path: string, title?: string) {
    // `deduplicated` (true when the path was already ingested, so no job was
    // queued) rides this route exactly as it does the web sibling below.
    // api/routes.py::register_local returns `job_id is None` for it.
    return this.request<{
      document: Record<string, unknown>;
      job_id: string | null;
      deduplicated: boolean;
    }>("POST", "/api/documents/local", { path, title: title ?? null });
  }

  registerWebDocument(url: string, title?: string) {
    return this.request<{
      document: Record<string, unknown>;
      job_id: string | null;
      deduplicated: boolean;
    }>("POST", "/api/documents/web", { url, title: title ?? null });
  }

  // --- graph -----------------------------------------------------------

  graphOverview() {
    return this.request<Record<string, unknown>>("GET", "/api/graph/overview");
  }

  expandNode(nodeId: string) {
    return this.request<Record<string, unknown>>(
      "GET",
      `/api/graph/expand?node_id=${encodeURIComponent(nodeId)}`,
    );
  }

  createNode(draft: NodeCreate) {
    return this.request<Record<string, unknown>>("POST", "/api/nodes", draft);
  }

  createEdge(draft: EdgeCreate) {
    return this.request<Record<string, unknown>>("POST", "/api/edges", draft);
  }

  confirmEdge(edgeId: string) {
    return this.request<Record<string, unknown>>("POST", `/api/edges/${edgeId}/confirm`);
  }

  search(query: string, scope: string[] = [], limit = 20) {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    for (const s of scope) params.append("scope", s);
    return this.request<Record<string, unknown>>("GET", `/api/search?${params}`);
  }

  // --- jobs / usage / exports -------------------------------------------------

  getJob(jobId: string) {
    return this.request<Record<string, unknown>>("GET", `/api/jobs/${jobId}`);
  }

  usageSummary(window: "day" | "month" = "day") {
    return this.request<Record<string, unknown>>(
      "GET",
      `/api/usage/summary?window=${window}`,
    );
  }

  createExport(req: ExportRequest) {
    return this.request<{ job_id: string }>("POST", "/api/exports", req);
  }

  /** The selectable contents of a document's tiered export (sections/figures/
   * tables + token costs) — backs the tiered-export picker. */
  exportInventory(docId: string) {
    return this.request<ExportInventory>(
      "GET",
      `/api/documents/${docId}/export-inventory`,
    );
  }

  /** The find -> bundle context engine: SYNCHRONOUS (returns the packaged
   * bundle directly, not a job id) — finds documents relevant to `query` and
   * packages them token-budgeted in the requested shape. */
  contextBundle(req: ContextBundleRequest) {
    return this.request<ContextBundleResult>("POST", "/api/context/bundle", req);
  }

  // --- AI streams -----------------------------------------------------------

  /** POST an explain request and iterate SSE events as they arrive. */
  async *explainStream(req: ExplainRequest): AsyncGenerator<StreamEvent> {
    yield* this.stream("/api/ai/explain", req);
  }

  async *ragStream(req: RagRequest): AsyncGenerator<StreamEvent> {
    yield* this.stream("/api/ai/rag", req);
  }

  private async *stream(path: string, body: unknown): AsyncGenerator<StreamEvent> {
    const init: RequestInit = {
      method: "POST",
      headers: { ...this.headers, Accept: "text/event-stream" },
      body: JSON.stringify(body),
    };
    const send = async () => {
      await this.beforeRequest?.();
      return this.fetch(`${this.base}${path}`, init);
    };

    // The backend authorizes the stream once (the route dependency) BEFORE any
    // event is yielded, so a 401/403 here always lands before the body starts —
    // re-issuing the whole request after a refresh loses nothing.
    let resp = await send();
    if ((resp.status === 401 || resp.status === 403) && this.onUnauthorized) {
      if (await this.onUnauthorized()) resp = await send();
    }
    yield* readSseStream(resp);
  }
}

export class GraphMindApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(`GraphMind API error ${status}: ${detail.slice(0, 200)}`);
  }
}
